// @flow
import React, { useState, useEffect, useCallback } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import LaunchIcon from '@material-ui/icons/Launch';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import PrimaryButton from '../../../view/common/component/PrimaryButton';
import SecondaryButton from '../../../view/common/component/SecondaryButton';
import { CUSTOM_LINK_PREFIX } from './constants';
import AttribTypeIcon from './AttribTypeIcon';
import {
    attribFactory,
    getPredefinedAttribs,
    createAttribObject,
    getFullUrl,
    getQueryParamValue,
    save as attribSave
} from './attribUtil';
import getCustomAttributionLinks from '../api/getCustomAttributionLinks';

type Props = {
    orgId: number,
    shareUrl: string,
    paramSeparator: string,
    productId: number
};

export default function AttribsTable({
    orgId,
    shareUrl,
    paramSeparator,
    productId
}: Props) {
    const [attribs, setAttribs] = useState([]);
    const [customAttribs, setCustomAttribs] = useState([]);
    const [attribIdsToDelete, setAttribIdsToDelete] = useState([]);
    const [changesMade, setChangesMade] = useState(false);
    const [
        originalCustomAttribsLength,
        setOriginalCustomAttribsLength
    ] = useState(0);
    const [errorsExist, setErrorsExist] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveButtonText, setSaveButtonText] = useState('Save Changes');
    const [wasLoaded, setWasLoaded] = useState(false);
    const [copiedLink, setCopiedLink] = useState({
        rowIndex: null,
        isCustom: null
    });
    const [timeoutId, setTimeoutId] = useState(null);

    useEffect(
        () => {
            if (attribs && attribs.length) {
                return;
            }
            setAttribs(attribFactory(getPredefinedAttribs(), false, orgId));
        },
        [attribs, orgId]
    );

    const determineChangesMade = useCallback(
        () => {
            if (customAttribs.length !== originalCustomAttribsLength) {
                setChangesMade(true);
                return;
            }
            let textLevelChanges = false;
            customAttribs.forEach((attrib: object) => {
                if (attrib.customText !== attrib.originalText) {
                    textLevelChanges = true;
                }
            });
            if (textLevelChanges) {
                setChangesMade(true);
                return;
            }
            setChangesMade(false);
        },
        [customAttribs, originalCustomAttribsLength]
    );

    useEffect(
        () => {
            const getCustomAttribs = async () => {
                if (wasLoaded) {
                    return;
                }
                const response = await getCustomAttributionLinks();
                if (!response || !response.length) {
                    return [];
                }
                setWasLoaded(true);
                const filtered = attribFactory(response || [], true, orgId);
                filtered.sort((a: object, b: object) => {
                    return a.order > b.order ? 1 : -1;
                });
                setCustomAttribs(filtered);
                setOriginalCustomAttribsLength(filtered.length);
                determineChangesMade();
            };
            getCustomAttribs();
            determineChangesMade();
        },
        [customAttribs, determineChangesMade, orgId, wasLoaded]
    );

    const addCustomAttrib = (event: object) => {
        event.preventDefault();
        const rows = [...customAttribs];
        const order = customAttribs.length;
        const customAttrib = createAttribObject(
            {
                order
            },
            true,
            orgId
        );
        rows.push(customAttrib);
        setCustomAttribs(rows);
        setErrorsExist(false);
    };

    const deleteCustomAttrib = (attrib: object) => {
        const rows = [...customAttribs];
        let filtered = rows.filter((r: object) => {
            return r.attribId !== attrib.attribId;
        });

        const toDelete = [
            ...attribIdsToDelete,
            ...rows
                .filter((r: object) => {
                    return !isNaN(r.attribId) && r.attribId === attrib.attribId;
                })
                .map((r: object) => r.attribId)
        ];
        setAttribIdsToDelete(toDelete);
        setCustomAttribs(filtered);
    };

    const handleCustomAttribChange = (object: event, attribId: number) => {
        const rows = [...customAttribs];
        const customAttrib = rows.find((r: object) => r.attribId === attribId);
        customAttrib.customText = event.target.value;
        customAttrib.attribQueryParam = `attrib=${getQueryParamValue(
            orgId,
            customAttrib.customText,
            CUSTOM_LINK_PREFIX
        )}`;
        customAttrib.error = false;
        setErrorsExist(false);
        setCustomAttribs(rows);
    };

    const updateCopyMessageText = (rowIndex: number, isCustom: boolean) => {
        setCopiedLink({ rowIndex, isCustom });
        clearTimeout(timeoutId);
        setTimeoutId(
            setTimeout(() => {
                setCopiedLink({ rowIndex: null, isCustom: null });
            }, 3000)
        );
    };

    const validateCustomAttribs = () => {
        let attribsClone = [...customAttribs];
        let hasErrors = false;
        attribsClone = attribsClone.map((a: object) => {
            if (!a.customText || !a.customText.length) {
                a.error = 'Missing content';
                hasErrors = true;
            }
            if (!/^[a-zA-Z0-9-_]+$/.test(a.customText)) {
                a.error = 'Please use alphanumeric and "-"';
                hasErrors = true;
            }
            return a;
        });
        setCustomAttribs(attribsClone);
        setErrorsExist(hasErrors);
        return !hasErrors;
    };

    const handleAttribSave = async () => {
        if (!validateCustomAttribs()) {
            return;
        }
        setSaving(true);
        const response = await attribSave(customAttribs, attribIdsToDelete);
        const filtered = attribFactory(response || [], true, orgId);
        filtered.sort((a: object, b: object) => (a.order > b.order ? 1 : -1));
        setOriginalCustomAttribsLength(filtered.length);
        determineChangesMade();
        setCustomAttribs(filtered);
        setTimeout(() => {
            // Saving can be too fast!
            setSaveButtonText('Saved!');
            setSaving(false);
            setTimeout(() => {
                setSaveButtonText('Save Changes');
            }, 1500);
        }, 1500);
    };

    return (
        <div>
            <Card variant="outlined">
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <b>Channel</b>
                                </TableCell>
                                <TableCell>
                                    <b>Share URL</b>
                                </TableCell>
                                <TableCell>
                                    <b>Copy to Clipboard</b>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attribs.map((row: object, index: number) => (
                                <TableRow
                                    key={`attrib-${row.typeEnum}`}
                                    className="thmp-attrib-row"
                                    style={
                                        index % 2
                                            ? { background: '#f9f9f9' }
                                            : { background: 'white' }
                                    }
                                >
                                    <TableCell>
                                        <div className="thmp-table-cell-inner">
                                            <AttribTypeIcon
                                                typeEnum={row.typeEnum}
                                            />
                                            <span className="thmp-attrib-row-type-label">
                                                {row.typeLabel}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="thmp-attrib-row-param">
                                            <span
                                            >{`${shareUrl}${paramSeparator}attrib=${
                                                row.attribQueryParam
                                            }`}</span>
                                            <a
                                                href={`${shareUrl}${paramSeparator}attrib=${
                                                    row.attribQueryParam
                                                }`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <LaunchIcon
                                                    fontSize="small"
                                                    className="thmp-attrib-row-link"
                                                />
                                            </a>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <CopyToClipboard
                                            text={getFullUrl(
                                                row,
                                                shareUrl,
                                                paramSeparator
                                            )}
                                            onCopy={updateCopyMessageText.bind(
                                                this,
                                                index,
                                                row.isCustom
                                            )}
                                        >
                                            <SecondaryButton
                                                size="large"
                                                variant="outlined"
                                            >
                                                <span>
                                                    {index ===
                                                        copiedLink.rowIndex &&
                                                    !copiedLink.isCustom
                                                        ? 'Copied'
                                                        : 'Copy'}
                                                </span>
                                            </SecondaryButton>
                                        </CopyToClipboard>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Button
                        style={{ marginTop: '20px' }}
                        variant="contained"
                        onClick={() => {
                            window.location = `/my-store/analytics/attributions${
                                productId ? `?team_id=${productId}` : ''
                            }`;
                        }}
                    >
                        View reporting
                    </Button>
                </CardContent>
            </Card>
            <Card className="thmp-sharelinks-bottom" variant="outlined">
                <CardContent>
                    <h3>custom affiliate links</h3>
                    <Table size="small">
                        <TableBody>
                            {customAttribs.map((row: object, index: number) => (
                                <TableRow
                                    key={`attrib-${row.attribId}`}
                                    className="thmp-attrib-row thmp-attrib-row--custom"
                                    style={
                                        index % 2
                                            ? { background: '#f9f9f9' }
                                            : { background: 'white' }
                                    }
                                >
                                    <TableCell>
                                        <div className="thmp-table-cell-inner">
                                            <AttribTypeIcon
                                                typeEnum={row.typeEnum}
                                            />
                                            <span className="thmp-attrib-row-type-label">
                                                {row.typeLabel}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="thmp-attrib-row-param">
                                            <span>
                                                {`${shareUrl}${paramSeparator}attrib=${orgId}-${CUSTOM_LINK_PREFIX}-`}
                                            </span>
                                            <TextField
                                                error={Boolean(row.error)}
                                                value={row.customText || ''}
                                                helperText={row.error || ''}
                                                onChange={(e: object) => {
                                                    handleCustomAttribChange.call(
                                                        this,
                                                        e,
                                                        row.attribId
                                                    );
                                                }}
                                            />
                                            {row.customText.length ? (
                                                <a
                                                    href={getFullUrl(
                                                        row,
                                                        shareUrl,
                                                        paramSeparator,
                                                        CUSTOM_LINK_PREFIX
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <LaunchIcon
                                                        fontSize="small"
                                                        className="thmp-attrib-row-link"
                                                    />
                                                </a>
                                            ) : null}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {row.customText.length ? (
                                            <CopyToClipboard
                                                text={getFullUrl(
                                                    row,
                                                    shareUrl,
                                                    paramSeparator,
                                                    CUSTOM_LINK_PREFIX
                                                )}
                                                onCopy={updateCopyMessageText.bind(
                                                    this,
                                                    index,
                                                    row.isCustom
                                                )}
                                            >
                                                <SecondaryButton
                                                    size="large"
                                                    variant="outlined"
                                                >
                                                    <span>
                                                        {index ===
                                                            copiedLink.rowIndex &&
                                                        copiedLink.isCustom
                                                            ? 'Copied'
                                                            : 'Copy'}
                                                    </span>
                                                </SecondaryButton>
                                            </CopyToClipboard>
                                        ) : null}
                                        <div className="thmp-attrib-delete">
                                            <SecondaryButton
                                                size="large"
                                                onClick={() => {
                                                    deleteCustomAttrib(row);
                                                }}
                                            >
                                                Delete
                                            </SecondaryButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <div>
                        <Grid
                            container
                            direction="row"
                            justify="flex-start"
                            alignItems="center"
                        >
                            <Grid item>
                                <div className="thmp-attrib-add">
                                    <SecondaryButton
                                        size="large"
                                        variant="outlined"
                                        onClick={addCustomAttrib}
                                    >
                                        <AddIcon /> Add custom link
                                    </SecondaryButton>
                                </div>
                            </Grid>
                            <Grid item xs={4} sm={4} lg={2}>
                                <div className="thmp-sharelinks-form-end">
                                    <PrimaryButton
                                        className="thmp-attrib-save"
                                        size="large"
                                        onClick={handleAttribSave}
                                        disabled={
                                            !changesMade ||
                                            errorsExist ||
                                            saving
                                        }
                                    >
                                        {saveButtonText}
                                    </PrimaryButton>
                                    {saving ? <CircularProgress /> : null}
                                </div>
                            </Grid>
                            <Grid>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        window.location = `/my-store/analytics/attributions${
                                            productId
                                                ? `?team_id=${productId}`
                                                : ''
                                        }`;
                                    }}
                                >
                                    View reporting
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
